import { useEffect, useRef } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { useToast } from '../components/Toast.js';

/**
 * AUTH-009 — where the payment provider sends the customer back.
 *
 * There is no billing screen any more: the account screen says which plan the
 * business is on and how much of it is used, and the plans themselves open in a
 * dialog over it. This route stays because the provider's success and cancel
 * URLs point at it and are registered on their side — it says what happened and
 * hands the customer back to the screen they left from.
 */
export function BillingPage() {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [params] = useSearchParams();

  const status = params.get('status');
  /**
   * StrictMode mounts effects twice in development, and the redirect below
   * unmounts this component immediately — without the guard the customer sees
   * the same payment announced twice.
   */
  const announced = useRef(false);
  useEffect(() => {
    if (announced.current) return;
    announced.current = true;
    if (status === 'success') toast.success(t('billing.paymentSuccess'));
    else if (status === 'cancelled') toast.show(t('billing.paymentCancelled'));
    // The webhook may have landed while the browser was still on the provider's
    // page, so whatever the account screen holds is already stale.
    void queryClient.invalidateQueries({ queryKey: ['billing'] });
  }, [status, toast, t, queryClient]);

  return <Navigate to="/profile" replace />;
}
